import { useCallback, useLayoutEffect, useRef, useState } from "react";

// An element's LAYOUT size and the cumulative CSS scale its ancestors apply to it.
//
// A slide is authored on a fixed 1600×900 logical canvas and painted with
// `transform: scale(k)` from the top left (`Slide.tsx`, §1.4). For everything the studio
// draws itself that is invisible — text, images and grids are laid out in logical units and
// the browser scales the result.
//
// **It is not invisible to a component that measures itself in SCREEN units**, which is what
// Excalidraw does: `updateDOMRect` takes `width`, `height`, `offsetLeft` and `offsetTop` from
// one `getBoundingClientRect()`, and the canvas element's CSS size is set to that width and
// height. Under `scale(0.55)` a container that lays out as 760 wide gets a 418px canvas in a
// 760px box — the drawing surface covers half the window, and a pointer at screen offset `dx`
// maps to canvas coordinate `dx` while the point visually under the cursor is at `dx / 0.55`.
// The error is zero at the transform origin and grows linearly with distance from it.
//
// **Not every embedded component needs this.** §26's gantt lays itself out in the slide's
// logical units like ordinary content and is better for it; using this hook there made four
// rounds of sizing bugs, because a measured transform goes stale silently (a transform changes
// no layout box, so nothing in the measurement's own inputs moves). Reach for it only when the
// component sizes ITSELF from the screen rect, as Excalidraw does.
//
// Returning both numbers lets the caller size the editor in SCREEN units and counter-scale it
// by `1 / scale`, so its layout size and its on-screen size are the same number again and its
// own arithmetic is correct without it knowing anything about slides.
//
// Measured on the element itself rather than walked up the tree: one `getBoundingClientRect`
// over one `offsetWidth` gives the cumulative factor of every transform above, whatever they
// are and however many there turn out to be.
export interface ScaledBox {
  // CONTENT-box size, in the logical units this element's own children are laid out in —
  // borders and scrollbars excluded, so it is the space a child actually gets.
  width: number;
  height: number;
  // Screen px per logical px. 1 when nothing above is scaled; 0 before the first measurement,
  // which callers must treat as "not ready" rather than dividing by it.
  scale: number;
}

const ZERO: ScaledBox = { width: 0, height: 0, scale: 0 };

export function useScaledBox<T extends HTMLElement>() {
  // A CALLBACK ref, not `useRef`. A ref object is captured once by the layout effect below,
  // so an element swapped out by a sibling branch — which is exactly what a component that
  // renders "at rest" and "editing" versions of the same box does — would leave the observer
  // watching a node that is no longer in the document, and the measurements would silently
  // freeze at whatever the previous element last reported.
  const [node, setNode] = useState<T | null>(null);
  const ref = useCallback((el: T | null) => setNode(el), []);
  const [box, setBox] = useState<ScaledBox>(ZERO);

  // Held in a ref so the every-render effect below can call the latest one without listing
  // `node` as a dependency and re-subscribing the observer on every render.
  const measureRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      // `offsetWidth` and `getBoundingClientRect().width` both INCLUDE borders, so their
      // ratio is the transform and nothing else. Dividing the rect by `clientWidth` instead
      // would fold the border width into the scale — a fraction of a percent, invisible, and
      // wrong in a number the caller divides by.
      const scale = node.offsetWidth > 0 ? rect.width / node.offsetWidth : 0;
      const width = node.clientWidth;
      const height = node.clientHeight;
      setBox((prev) =>
        prev.width === width && prev.height === height && prev.scale === scale
          ? // Same numbers: keep the previous object so effects keyed on this box do not
            // re-run on every observer tick.
            prev
          : { width, height, scale },
      );
    };
    measureRef.current = measure;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    // Seed before paint, so the first frame is not a zero-size one — the same reason
    // `useElementWidth` uses a layout effect.
    measure();
    return () => {
      observer.disconnect();
      measureRef.current = null;
    };
  }, [node]);

  // **A ResizeObserver is not enough for `scale`, only for `width`/`height`.** It reports
  // layout boxes, and an ancestor's `transform` does not change any of them: dragging the
  // deck's resizable panels changes the slide's scale factor while every element inside its
  // logical canvas keeps exactly the same `clientWidth`. The observer stays silent and the
  // scale we hold goes stale — which, for a caller dividing by it, re-opens the very offset
  // this hook exists to close, halfway through an editing session.
  //
  // Re-measuring on every render covers it: the slide re-renders when its scale changes, so
  // its descendants do too. One `getBoundingClientRect` per render is cheap, and the setter
  // above returns the previous object when nothing moved, so this cannot loop.
  useLayoutEffect(() => {
    measureRef.current?.();
  });

  return [ref, box] as const;
}
